import React, { useEffect, useState } from 'react'
import { subMonths, format } from 'date-fns'
import {
    faArrowLeft,
    faCheck,
    faTimes,
    faCreditCard,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Button,
    // CircularProgress,
    Typography,
    Dialog,
    DialogContent,
} from '@mui/material'
import AlertSnackbar, {
    alertSnackbarContentProps,
} from 'components/AlertSnackbar'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import idDirectory from './idAttributes'
import {
    InitiateDisputeStep1,
    TransactionInfoStep2,
    AdditionalInfoStep3,
    FinalStep4,
} from './steps'
import useCaseBuilderIssuerVM from './CaseBuilderIssuer.vm'

export const CaseBuilderIssuerInfo = ({
    selectedDisputeReasonId,
    formInfo,
}: {
    selectedDisputeReasonId: number | null
    formInfo: { [key: string]: any }
}) => {
    const showFraudUI = selectedDisputeReasonId
        ? [1, 2].includes(selectedDisputeReasonId)
        : undefined

    return (
        <div className="emp-caseBuilderIssuerInfo-infoContainer">
            <Typography className="emp-caseBuilderIssuerInfo-header">
                Transaction ID:
            </Typography>
            <Typography className="emp-caseBuilderIssuerInfo-subHeader">
                {formInfo.transactionId ?? 'N/A'}
            </Typography>

            <div className="emp-caseBuilderIssuerInfo-dotIcon" />

            <Typography className="emp-caseBuilderIssuerInfo-header">
                Dispute Type:
            </Typography>
            <Typography className="emp-caseBuilderIssuerInfo-subHeader">
                {formInfo.disputeType === 'Raise a Chargeback'
                    ? 'Chargeback'
                    : formInfo.disputeType ?? 'N/A'}
            </Typography>

            <div className="emp-caseBuilderIssuerInfo-dotIcon" />

            <Typography className="emp-caseBuilderIssuerInfo-header">
                Dispute Reason:
            </Typography>
            <Typography className="emp-caseBuilderIssuerInfo-subHeader">
                {showFraudUI ? 'Fraud' : 'Non-Fraud'}
            </Typography>
        </div>
    )
}

interface ICaseBuilderIssuerProps {
    openCaseBuilderIssuer: boolean
    onClose: () => void
    testId: string
    className?: string
}

const CaseBuilderIssuer = ({
    openCaseBuilderIssuer,
    onClose,
    testId,
    className = '',
}: ICaseBuilderIssuerProps) => {
    const { clientTheme } = useThemeStyling()
    const [
        isEnableAutofillChecked,
        setIsEnableAutofillChecked,
    ] = useState<boolean>(true)
    const [alertSnackbarOpen, setAlertSnackbarOpen] = useState<boolean>(false)
    const [
        alertSnackbarProps,
        setAlertSnackbarProps,
    ] = useState<alertSnackbarContentProps>({})
    const [selectedDisputeReasonId, setSelectedDisputeReasonId] = useState<
        number | null
    >(null)
    const [formInfo, setFormInfo] = useState<{ [key: string]: any }>({})
    const [enableSaveAndContinue, setEnableSaveAndContinue] = useState<boolean>(
        false
    )

    useEffect(() => {
        if (openCaseBuilderIssuer) {
            const initialFormInfo = !isEnableAutofillChecked
                ? {
                      merchantName: '',
                      merchantDescriptor: '',
                      transactionDate: '',
                      transactionAmount: '',
                      currency: '',
                      binFirst6digits: '',
                      binLast4Digits: '',
                      authId: '',
                      disputeAmount: '',
                  }
                : {
                      merchantName: 'ABC Airlines',
                      merchantDescriptor: 'ABCAirlines_8881213333*pcl',
                      // Date 1 month before today:
                      transactionDate: format(
                          subMonths(new Date(), 1),
                          'yyyy-MM-dd'
                      ),
                      transactionAmount: '31.05',
                      currency: 'EUR',
                      binFirst6digits: '453275',
                      binLast4Digits: '4821',
                      authId: '292750',
                      disputeAmount: '31.05',
                  }
            setFormInfo({ ...formInfo, ...initialFormInfo })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnableAutofillChecked, openCaseBuilderIssuer])

    const {
        currentStep,
        setCurrentStep,
        accountNumberList,
        disputeReasonList,
        currencyList,
        productServiceList,
        typeList,
        fileTemplateList,
        isLoadingCurrencyList,
        isSubmittingFinal,
        handleFinalStep,
    } = useCaseBuilderIssuerVM()

    const handleOnCloseModal = () => {
        onClose()
        setCurrentStep(1)
        setSelectedDisputeReasonId(null)
        setFormInfo({})
    }

    const handleChangeCaseBuilderIssuerStep = (move: 'back' | 'continue') => {
        if (currentStep === 3) handleFinalStep()
        if (move === 'continue') {
            setCurrentStep(currentStep + 1)
        }
        if (move === 'back' && currentStep !== 1) {
            setCurrentStep(currentStep - 1)
        }
        setEnableSaveAndContinue(false)
    }

    const handleBack = () => {
        handleChangeCaseBuilderIssuerStep('back')
    }
    const handleSaveAndContinue = () => {
        handleChangeCaseBuilderIssuerStep('continue')
    }

    const handleChangeFormField = (accessor: string, input: any) => {
        setFormInfo({
            ...formInfo,
            [accessor]: input,
        })
    }
    const handleChangeFormSwitch = (accessor: string, input: boolean) => {
        setFormInfo({
            ...formInfo,
            [accessor]: input,
        })
    }

    return (
        <Dialog
            open={openCaseBuilderIssuer}
            onClose={(_, reason) => {
                if (reason && reason === 'backdropClick') return
                handleOnCloseModal()
            }}
            id={`${idDirectory.caseBuilderIssuer.divRoot}-${testId}`}
            className={className}
            maxWidth={'md'}
            fullWidth
        >
            <div
                className={'emp-caseBuilderIssuer-topbarContainer'}
                style={{
                    backgroundColor: clientTheme.caseBuilder.color,
                }}
                id={`${idDirectory.caseBuilderIssuer.divTopbar}-${testId}`}
            >
                <div
                    className={'emp-caseBuilderIssuer-topbarTitleContainer'}
                    id={`${idDirectory.caseBuilderIssuer.divTopbarTitle}-${testId}`}
                >
                    <FontAwesomeIcon
                        icon={faCreditCard}
                        className={'emp-caseBuilderIssuer-creditCardIcon'}
                    />
                    <Typography variant="h3">
                        Case Builder | Dispute Assistant
                    </Typography>
                </div>
                <div
                    id={`${idDirectory.caseBuilderIssuer.divTopbarClose}-${testId}`}
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        className={`emp-caseBuilderIssuer-closeIcon `}
                        onClick={handleOnCloseModal}
                        id={`${idDirectory.caseBuilderIssuer.iconClose}-${testId}`}
                    />
                </div>
            </div>
            {currentStep !== 4 ? (
                <>
                    {[2, 3].includes(currentStep) && (
                        <CaseBuilderIssuerInfo
                            selectedDisputeReasonId={selectedDisputeReasonId}
                            formInfo={formInfo}
                        />
                    )}
                    <DialogContent
                        className={'emp-caseBuilderIssuer-mainContainer'}
                        id={`${idDirectory.caseBuilderIssuer.divMain}-${testId}`}
                    >
                        {currentStep === 1 && (
                            <InitiateDisputeStep1
                                accountNumberList={accountNumberList}
                                disputeReasonList={disputeReasonList}
                                setSelectedDisputeReasonId={
                                    setSelectedDisputeReasonId
                                }
                                formInfo={formInfo}
                                handleChangeFormField={handleChangeFormField}
                                isEnableAutofillChecked={
                                    isEnableAutofillChecked
                                }
                                setIsEnableAutofillChecked={
                                    setIsEnableAutofillChecked
                                }
                                setEnableSaveAndContinue={
                                    setEnableSaveAndContinue
                                }
                            />
                        )}
                        {currentStep === 2 && (
                            <TransactionInfoStep2
                                currencyList={currencyList}
                                disputeReasonList={disputeReasonList}
                                isLoadingCurrencyList={isLoadingCurrencyList}
                                formInfo={formInfo}
                                handleChangeFormField={handleChangeFormField}
                                setSelectedDisputeReasonId={
                                    setSelectedDisputeReasonId
                                }
                                setEnableSaveAndContinue={
                                    setEnableSaveAndContinue
                                }
                            />
                        )}
                        {currentStep === 3 && (
                            <AdditionalInfoStep3
                                setAlertSnackbarProps={setAlertSnackbarProps}
                                setAlertSnackbarOpen={setAlertSnackbarOpen}
                                productServiceList={productServiceList}
                                typeList={typeList}
                                fileTemplateList={fileTemplateList}
                                selectedDisputeReasonId={
                                    selectedDisputeReasonId
                                }
                                formInfo={formInfo}
                                handleChangeFormSwitch={handleChangeFormSwitch}
                                handleChangeFormField={handleChangeFormField}
                                setEnableSaveAndContinue={
                                    setEnableSaveAndContinue
                                }
                            />
                        )}
                    </DialogContent>
                    <div
                        id={`${idDirectory.caseBuilderIssuer.divActions}-${testId}`}
                        className={`emp-caseBuilderIssuer-footerContainer`}
                    >
                        <div>
                            {currentStep !== 1 && (
                                <Button
                                    variant="text"
                                    color="secondary"
                                    id={`${idDirectory.caseBuilderIssuer.btnBack}-${testId}`}
                                    className={'emp-caseBuilderIssuer-backBtn'}
                                    onClick={handleBack}
                                    // disabled={isLoading}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowLeft}
                                        className={
                                            'emp-caseBuilderIssuer-btnIcons'
                                        }
                                    />
                                    Back
                                </Button>
                            )}
                        </div>
                        <div
                            className={
                                'emp-caseBuilderIssuer-footerRightContainer'
                            }
                        >
                            {![1, 4].includes(currentStep) && (
                                <Button
                                    id={`${idDirectory.caseBuilderIssuer.btnsaveDraft}-${testId}`}
                                    className={
                                        'emp-caseBuilderIssuer-saveDraftBtn'
                                    }
                                    onClick={handleOnCloseModal}
                                    variant="outlined"
                                    color="secondary"
                                >
                                    Save for Later
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="secondary"
                                id={`${idDirectory.caseBuilderIssuer.btnSaveContinue}-${testId}`}
                                className={
                                    'emp-caseBuilderIssuer-saveContinueBtn'
                                }
                                onClick={handleSaveAndContinue}
                                disabled={!enableSaveAndContinue}
                            >
                                {/* {isSubmitting ? (
                            <>
                                Submitting...{' '}
                                <CircularProgress
                                    className={
                                        'emp-caseBuilderIssuer-circularProgress'
                                    }
                                    color="secondary"
                                />
                            </>
                        ) : ( */}
                                <>
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className={
                                            'emp-caseBuilderIssuer-btnIcons'
                                        }
                                    />
                                    {currentStep !== 3
                                        ? 'Save and Continue'
                                        : 'Complete'}
                                </>
                                {/* )} */}
                            </Button>
                        </div>
                    </div>
                </>
            ) : (
                <FinalStep4 isSubmittingFinal={isSubmittingFinal} />
            )}

            <AlertSnackbar
                content={alertSnackbarProps}
                open={alertSnackbarOpen}
                onClose={() => setAlertSnackbarOpen(false)}
                showCloseIcon
            />
        </Dialog>
    )
}

export default CaseBuilderIssuer
